import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Empty, Tooltip } from 'antd';
import { DownOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';
import StatusTag from '../../../components/ui/StatusTag';
import { ProductionOrder } from '../../../types/ProductionOrder';
import {
  barGeometry,
  ColorMode,
  dayColumns,
  isLocked,
  isOverdue,
  isWeekend,
  LABEL_WIDTH,
  ROW_HEIGHT,
  sectionColorFor,
  sectionConflictKeys,
  statusColor,
  todayOffset,
} from './planning-board.util';

export type GroupBy = 'order' | 'section';

interface PlanningBoardProps {
  orders: ProductionOrder[];
  rangeStart: Dayjs;
  rangeEnd: Dayjs;
  dayWidth: number;
  groupBy: GroupBy;
  colorMode: ColorMode;
  onSelectOrder: (order: ProductionOrder) => void;
  onReschedule: (order: ProductionOrder, start: string, end: string) => void;
  onRescheduleSection: (
    order: ProductionOrder,
    sectionIndex: number,
    start: string,
    end: string,
  ) => void;
}

type DragMode = 'move' | 'resize-l' | 'resize-r';
interface DragState {
  key: string;
  mode: DragMode;
  startX: number;
  origStart: Dayjs;
  origEnd: Dayjs;
  deltaDays: number;
  apply: (start: string, end: string) => void;
}

interface BarOpts {
  key: string;
  status: string;
  start: string | null;
  end: string | null;
  color: string;
  label?: string;
  thin?: boolean;
  conflict?: boolean;
  locked?: boolean;
  onClick?: () => void;
  apply?: (start: string, end: string) => void;
  tooltip?: React.ReactNode;
}

const handleStyle = (side: 'left' | 'right'): React.CSSProperties => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  [side]: 0,
  width: 7,
  cursor: 'ew-resize',
  background: 'rgba(0,0,0,0.15)',
});

const PlanningBoard: React.FC<PlanningBoardProps> = ({
  orders,
  rangeStart,
  rangeEnd,
  dayWidth,
  groupBy,
  colorMode,
  onSelectOrder,
  onReschedule,
  onRescheduleSection,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const sectionFill = (
    status: string,
    code?: string | null,
    boardColor?: string | null,
  ) => {
    if (colorMode === 'process' && boardColor) return boardColor;
    if (colorMode === 'section' && code) return sectionColorFor(code);
    return statusColor(status);
  };
  const [drag, setDrag] = useState<DragState | null>(null);

  const cols = useMemo(() => dayColumns(rangeStart, rangeEnd), [rangeStart, rangeEnd]);
  const timelineWidth = cols.length * dayWidth;
  const todayX = todayOffset(rangeStart, rangeEnd, dayWidth);
  const conflictKeys = useMemo(() => sectionConflictKeys(orders), [orders]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const deltaDays = Math.round((e.clientX - drag.startX) / dayWidth);
      setDrag((d) => (d && d.deltaDays !== deltaDays ? { ...d, deltaDays } : d));
    };
    const onUp = () => {
      setDrag((d) => {
        if (d && d.deltaDays !== 0) {
          let s = d.origStart;
          let e = d.origEnd;
          if (d.mode === 'move') {
            s = s.add(d.deltaDays, 'day');
            e = e.add(d.deltaDays, 'day');
          } else if (d.mode === 'resize-r') {
            e = e.add(d.deltaDays, 'day');
            if (e.isBefore(s)) e = s;
          } else {
            s = s.add(d.deltaDays, 'day');
            if (s.isAfter(e)) s = e;
          }
          d.apply(s.format('YYYY-MM-DD'), e.format('YYYY-MM-DD'));
        }
        return null;
      });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp, { once: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, [drag, dayWidth]);

  const startDrag = (e: React.PointerEvent, o: BarOpts, mode: DragMode) => {
    if (!o.apply || o.locked || !o.start || !o.end) return;
    e.stopPropagation();
    setDrag({
      key: o.key,
      mode,
      startX: e.clientX,
      origStart: dayjs(o.start),
      origEnd: dayjs(o.end),
      deltaDays: 0,
      apply: o.apply,
    });
  };

  const gridAndToday = (
    <>
      {cols.map((d, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: i * dayWidth,
            top: 0,
            bottom: 0,
            width: dayWidth,
            borderRight: dayWidth >= 12 ? '1px solid #f0f0f0' : 'none',
            background: isWeekend(d) && dayWidth >= 12 ? '#fafafa' : 'transparent',
          }}
        />
      ))}
      {todayX >= 0 && (
        <div
          style={{
            position: 'absolute',
            left: todayX,
            top: 0,
            bottom: 0,
            width: 2,
            marginLeft: -1,
            background: '#ff4d4f',
            zIndex: 1,
          }}
        />
      )}
    </>
  );

  // draggable bar (used by both layouts)
  const renderBar = (o: BarOpts, rowHeight: number) => {
    const geo = barGeometry(o.start, o.end, rangeStart, rangeEnd, dayWidth);
    if (!geo.visible) return null;
    const dragging = drag?.key === o.key ? drag : null;
    let left = geo.left;
    let width = geo.width;
    if (dragging) {
      const shift = dragging.deltaDays * dayWidth;
      if (dragging.mode === 'move') left += shift;
      else if (dragging.mode === 'resize-r') width += shift;
      else {
        left += shift;
        width -= shift;
      }
    }
    const canDrag = !!o.apply && !o.locked;
    const overdue = isOverdue(o.end, o.status);
    const height = o.thin ? 16 : rowHeight - 12;
    return (
      <Tooltip title={o.tooltip}>
        <div
          onPointerDown={(e) => canDrag && startDrag(e, o, 'move')}
          onClick={o.onClick}
          style={{
            position: 'absolute',
            left,
            width: Math.max(width, dayWidth * 0.5),
            top: o.thin ? rowHeight / 2 - 8 : 6,
            height,
            background: o.color,
            borderRadius: 4,
            border: o.conflict ? '2px solid #ff4d4f' : 'none',
            color: '#fff',
            fontSize: 12,
            lineHeight: `${height}px`,
            padding: '0 10px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            cursor: canDrag ? 'grab' : o.onClick ? 'pointer' : 'default',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            opacity: dragging ? 0.8 : 1,
            zIndex: 1,
          }}
        >
          {canDrag && (
            <span onPointerDown={(e) => startDrag(e, o, 'resize-l')} style={handleStyle('left')} />
          )}
          {o.conflict && <WarningOutlined style={{ marginRight: 4 }} />}
          {o.label}
          {overdue && (
            <span
              title={'Overdue'}
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#ff4d4f',
                border: '1px solid #fff',
              }}
            />
          )}
          {canDrag && (
            <span onPointerDown={(e) => startDrag(e, o, 'resize-r')} style={handleStyle('right')} />
          )}
        </div>
      </Tooltip>
    );
  };

  const Header = (
    <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
      <div style={labelCellStyle(600)}>
        {groupBy === 'order' ? 'Production Order' : 'Section'}
      </div>
      <div style={{ position: 'relative', width: timelineWidth, height: 40 }}>
        {cols.map((d, i) => {
          const show =
            dayWidth >= 30 ? true : dayWidth >= 12 ? d.day() === 1 : d.date() === 1;
          if (!show) return null;
          const top = dayWidth >= 30 ? d.format('DD') : dayWidth >= 12 ? d.format('DD') : d.format('MMM');
          const bot = dayWidth >= 30 ? d.format('dd') : dayWidth >= 12 ? d.format('MMM') : d.format('YY');
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: i * dayWidth,
                width: Math.max(dayWidth, 34),
                textAlign: 'center',
                fontSize: 11,
                paddingTop: 4,
                color: isWeekend(d) ? '#bbb' : '#666',
              }}
            >
              <div style={{ fontWeight: 600 }}>{top}</div>
              <div>{bot}</div>
            </div>
          );
        })}
        {todayX >= 0 && (
          <div
            style={{
              position: 'absolute',
              left: todayX,
              top: 0,
              bottom: 0,
              width: 2,
              marginLeft: -1,
              background: '#ff4d4f',
            }}
          />
        )}
      </div>
    </div>
  );

  if (!orders.length) {
    return (
      <div className={'border rounded bg-white p-8'}>
        <Empty description={'No production orders in this date range'} />
      </div>
    );
  }

  return (
    <div
      className={'border rounded bg-white'}
      style={{ overflow: 'auto', userSelect: drag ? 'none' : 'auto' }}
    >
      <div style={{ minWidth: LABEL_WIDTH + timelineWidth }}>
        {Header}
        {groupBy === 'order'
          ? renderOrderRows()
          : renderSectionLanes()}
      </div>
    </div>
  );

  // ---------- ORDER layout ----------
  function renderOrderRows() {
    return orders.map((order) => {
      const isOpen = expanded.has(order.id);
      const locked = isLocked(order.status);
      return (
        <div key={order.id}>
          <div style={{ display: 'flex', borderBottom: '1px solid #f5f5f5' }}>
            <div style={labelCellStyle()}>
              {order.sections?.length ? (
                <span onClick={() => toggle(order.id)} style={{ cursor: 'pointer', color: '#999' }}>
                  {isOpen ? <DownOutlined /> : <RightOutlined />}
                </span>
              ) : (
                <span style={{ width: 14 }} />
              )}
              <span
                onClick={() => onSelectOrder(order)}
                style={{ fontWeight: 600, cursor: 'pointer', flex: 1 }}
                className={'truncate'}
              >
                {order.code}
              </span>
              <StatusTag status={order.status} />
            </div>
            <div style={{ position: 'relative', width: timelineWidth, height: ROW_HEIGHT }}>
              {gridAndToday}
              {renderBar(
                {
                  key: `o:${order.id}`,
                  status: order.status,
                  start: order.productionStart,
                  end: order.productionEnd,
                  color: statusColor(order.status),
                  label: order.code,
                  locked,
                  onClick: () => onSelectOrder(order),
                  apply: (s, e) => onReschedule(order, s, e),
                  tooltip: barTooltip(order.code, order.productionStart, order.productionEnd, order.status, !locked),
                },
                ROW_HEIGHT,
              )}
            </div>
          </div>

          {isOpen &&
            order.sections?.map((sec, idx) => (
              <div key={sec.id ?? idx} style={{ display: 'flex', borderBottom: '1px solid #fafafa' }}>
                <div style={{ ...labelCellStyle(), background: '#fbfbfb', paddingLeft: 30, height: ROW_HEIGHT - 6, fontSize: 12 }}>
                  <span className={'truncate'} style={{ flex: 1, color: '#555' }}>
                    {sec.section?.code ?? sec.section?.description ?? 'Section'}
                  </span>
                  <StatusTag status={sec.status} />
                </div>
                <div style={{ position: 'relative', width: timelineWidth, height: ROW_HEIGHT - 6, background: '#fbfbfb' }}>
                  {gridAndToday}
                  {renderBar(
                    {
                      key: `s:${order.id}:${idx}`,
                      status: sec.status,
                      start: sec.productionStart,
                      end: sec.productionEnd,
                      color: sectionFill(sec.status, sec.section?.code, sec.section?.prodProcess?.boardColor),
                      thin: true,
                      locked,
                      onClick: () => onSelectOrder(order),
                      apply: (s, e) => onRescheduleSection(order, idx, s, e),
                      tooltip: barTooltip(sec.section?.code, sec.productionStart, sec.productionEnd, sec.status, !locked),
                    },
                    ROW_HEIGHT - 6,
                  )}
                </div>
              </div>
            ))}
        </div>
      );
    });
  }

  // ---------- SECTION (swimlane) layout ----------
  function renderSectionLanes() {
    const lanes = new Map<string, { label: string; bars: { order: ProductionOrder; idx: number }[] }>();
    orders.forEach((o) =>
      o.sections?.forEach((s, idx) => {
        const key = s.section?.code ?? s.section?.id ?? 'section';
        const lane = lanes.get(key) ?? {
          label: s.section?.code ?? s.section?.description ?? key,
          bars: [],
        };
        lane.bars.push({ order: o, idx });
        lanes.set(key, lane);
      }),
    );

    return Array.from(lanes.entries()).map(([key, lane]) => {
      return (
        <div key={key} style={{ display: 'flex', borderBottom: '1px solid #f5f5f5' }}>
          <div style={labelCellStyle(600)}>
            <span className={'truncate'} style={{ flex: 1 }}>{lane.label}</span>
            <span style={{ color: '#999', fontSize: 12 }}>{lane.bars.length}</span>
          </div>
          <div style={{ position: 'relative', width: timelineWidth, height: ROW_HEIGHT }}>
            {gridAndToday}
            {lane.bars.map(({ order, idx }) => {
              const sec = order.sections[idx];
              const locked = isLocked(order.status) || isLocked(sec.status);
              return (
                <React.Fragment key={`${order.id}:${idx}`}>
                  {renderBar(
                    {
                      key: `l:${order.id}:${idx}`,
                      status: sec.status,
                      start: sec.productionStart,
                      end: sec.productionEnd,
                      color: sectionFill(sec.status, sec.section?.code, sec.section?.prodProcess?.boardColor),
                      label: order.code,
                      conflict: conflictKeys.has(`${order.id}:${idx}`),
                      locked,
                      onClick: () => onSelectOrder(order),
                      apply: (s, e) => onRescheduleSection(order, idx, s, e),
                      tooltip: barTooltip(order.code, sec.productionStart, sec.productionEnd, sec.status, !locked),
                    },
                    ROW_HEIGHT,
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      );
    });
  }
};

function labelCellStyle(fontWeight?: number): React.CSSProperties {
  return {
    width: LABEL_WIDTH,
    flex: '0 0 auto',
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: '#fff',
    borderRight: '1px solid #f0f0f0',
    padding: '0 8px',
    height: ROW_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: fontWeight ?? 400,
  };
}

function barTooltip(
  code: string | undefined,
  start: string | null,
  end: string | null,
  status: string,
  draggable: boolean,
) {
  return (
    <div>
      <div>{code}</div>
      <div>
        {start} → {end}
      </div>
      <div>{status}</div>
      {draggable && <div style={{ opacity: 0.7 }}>drag to reschedule</div>}
    </div>
  );
}

export default PlanningBoard;
